import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash2, KeyRound, Power, Users, Loader2, Shield, Camera, X as XIcon, Pencil } from "lucide-react";
import { toast } from "sonner";

const PERM_CATALOG = [
  { key: "player.manage", label: "Player Management", hint: "block / withdraw perms / password / 2FA / bets" },
  { key: "tx.deposit.decide", label: "Approve / Reject Site Deposits" },
  { key: "tx.withdrawal.decide", label: "Approve / Reject Site Withdrawals" },
  { key: "tx.agent_withdrawal.decide", label: "Approve / Reject Agent Withdrawals" },
  { key: "kyc.decide", label: "Approve / Reject KYC Submissions" },
];

// Resolve a stored avatar path (e.g. `/api/live-support/files/xxx.png`) to a fully-qualified URL
function avatarSrc(path) {
  if (!path) return "";
  if (/^https?:/i.test(path)) return path;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${path}`;
}

export default function LiveSupportAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPerms, setEditingPerms] = useState(null); // agent obj
  const [editingProfile, setEditingProfile] = useState(null); // agent obj (name + designation)
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null); // agent id currently uploading

  const load = async () => {
    try {
      const { data } = await api.get("/admin/live-support/agents");
      setAgents(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load agents");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (a) => {
    try {
      await api.put(`/admin/live-support/agents/${a.id}`, { active: !a.active });
      toast.success(`${a.phone} ${!a.active ? "unblocked" : "blocked"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const resetPwd = async (a) => {
    const pw = window.prompt(`New password for ${a.phone}:`, "");
    if (!pw) return;
    if (pw.length < 4) { toast.error("Password too short"); return; }
    try {
      await api.post(`/admin/live-support/agents/${a.id}/password`, { password: pw });
      toast.success("Password updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Permanently remove agent ${a.phone}? Their active sessions will be released.`)) return;
    try {
      await api.delete(`/admin/live-support/agents/${a.id}`);
      toast.success("Agent removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const pickAvatar = (a) => {
    setUploadingFor(a.id);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onAvatarFileChange = async (e) => {
    const file = e.target?.files?.[0];
    const aid = uploadingFor;
    setUploadingFor(null);
    if (!file || !aid) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo too large (max 8 MB)"); return; }
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/live-support/agents/${aid}/avatar`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Agent photo updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    }
  };

  const clearAvatar = async (a) => {
    if (!a.avatar_url) return;
    if (!window.confirm(`Remove photo for ${a.name || a.phone}?`)) return;
    try {
      await api.delete(`/admin/live-support/agents/${a.id}/avatar`);
      toast.success("Photo removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="ls-agents-page">
      <PageHeader title="Live Support Agents" subtitle="Create human chat agents. They sign in at /livechat with their phone number + password." />

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 inline-flex items-center gap-2">
          <Users size={15} className="text-emerald-600" />
          <span>{agents.length} agent{agents.length === 1 ? "" : "s"}</span>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="ls-agent-add"
                className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm inline-flex items-center gap-2">
          <Plus size={15} /> Create Agent
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-600 font-bold">
              <th className="px-4 py-2.5">Agent</th>
              <th className="px-4 py-2.5">Phone (Login)</th>
              <th className="px-4 py-2.5">Active Sessions</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Permissions</th>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-gray-500"><Loader2 size={14} className="animate-spin inline mr-2" />Loading...</td></tr>
            ) : agents.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-gray-500">No agents yet. Click &quot;Create Agent&quot; to add one.</td></tr>
            ) : agents.map((a) => (
              <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {a.avatar_url ? (
                      <img
                        src={avatarSrc(a.avatar_url)}
                        alt={a.name || a.phone}
                        className="h-9 w-9 rounded-full object-cover ring-2 ring-emerald-200"
                        data-testid={`ls-agent-avatar-img-${a.phone}`}
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-emerald-500 text-white font-bold text-xs flex items-center justify-center" data-testid={`ls-agent-avatar-letter-${a.phone}`}>
                        {(a.name || a.phone).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{a.name || a.phone}</div>
                      {a.designation && (
                        <div className="text-[11px] text-emerald-700 font-medium truncate" data-testid={`ls-agent-designation-${a.phone}`}>{a.designation}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-gray-700">{a.phone}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">
                    {a.active_sessions || 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {a.active
                    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">Active</span>
                    : <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 text-xs font-bold">Blocked</span>}
                </td>
                <td className="px-4 py-3">
                  {(a.permissions && a.permissions.length > 0) ? (
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {a.permissions.slice(0, 3).map((p) => (
                        <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-[10px] font-bold">{p}</span>
                      ))}
                      {a.permissions.length > 3 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold">+{a.permissions.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-400 italic">No power (read-only)</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setEditingProfile(a)} className="h-8 px-2 rounded-sm border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-edit-${a.phone}`} title="Edit name &amp; designation">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => pickAvatar(a)} className="h-8 px-2 rounded-sm border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-photo-${a.phone}`} title="Upload / change agent photo">
                      <Camera size={12} /> Photo
                    </button>
                    {a.avatar_url && (
                      <button onClick={() => clearAvatar(a)} className="h-8 px-2 rounded-sm border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-photo-clear-${a.phone}`} title="Remove photo">
                        <XIcon size={12} />
                      </button>
                    )}
                    <button onClick={() => toggle(a)} className="h-8 px-2 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-toggle-${a.phone}`}>
                      <Power size={12} /> {a.active ? "Block" : "Unblock"}
                    </button>
                    <button onClick={() => setEditingPerms(a)} className="h-8 px-2 rounded-sm border border-violet-200 text-violet-700 hover:bg-violet-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-perms-${a.phone}`}>
                      <Shield size={12} /> Permissions
                    </button>
                    <button onClick={() => resetPwd(a)} className="h-8 px-2 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-pw-${a.phone}`}>
                      <KeyRound size={12} /> Reset PW
                    </button>
                    <button onClick={() => remove(a)} className="h-8 px-2 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold inline-flex items-center gap-1" data-testid={`ls-agent-del-${a.phone}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900 max-w-3xl">
        <div className="font-bold mb-1">How it works</div>
        <ul className="list-disc list-inside space-y-1 text-emerald-800 text-[13px]">
          <li>Create agents with a unique phone number (this is their login id) and a password.</li>
          <li>Use <b>Photo</b> to upload a profile picture — users will see the agent&apos;s photo + name in the chat once the agent joins.</li>
          <li>Agents sign in at <code className="bg-white px-1 rounded">{window.location.origin}/livechat</code> with phone + password.</li>
          <li>Use <b>Block</b> to instantly revoke their login, or <b>Reset PW</b> to issue a new password.</li>
        </ul>
      </div>

      {/* Hidden file input — shared for all rows */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={onAvatarFileChange}
        data-testid="ls-agent-avatar-file"
      />

      {showCreate && <CreateAgentModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editingPerms && <PermsModal agent={editingPerms} onClose={() => setEditingPerms(null)} onSaved={() => { setEditingPerms(null); load(); }} />}
      {editingProfile && <EditProfileModal agent={editingProfile} onClose={() => setEditingProfile(null)} onSaved={() => { setEditingProfile(null); load(); }} />}
    </div>
  );
}

function EditProfileModal({ agent, onClose, onSaved }) {
  const [name, setName] = useState(agent.name || "");
  const [designation, setDesignation] = useState(agent.designation || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/live-support/agents/${agent.id}`, {
        name: name.trim(),
        designation: designation.trim(),
      });
      toast.success("Agent profile updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={save} className="bg-white rounded-sm shadow-xl w-full max-w-md p-5" data-testid="ls-agent-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-gray-900">Edit Agent Profile</div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Display Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
                   placeholder="e.g. Rafiq Hasan"
                   data-testid="ls-agent-edit-name"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Designation / Role</label>
            <input value={designation} onChange={(e) => setDesignation(e.target.value)} maxLength={60}
                   placeholder="e.g. Senior Support Manager"
                   data-testid="ls-agent-edit-designation"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
            <div className="text-[11px] text-gray-500 mt-1">Shown to the user under the agent&apos;s name when this agent joins their chat.</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ls-agent-edit-save"
                  className="h-10 px-4 rounded-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PermsModal({ agent, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(agent.permissions || []));
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/live-support/agents/${agent.id}`, { permissions: Array.from(selected) });
      toast.success(`Permissions updated for ${agent.name || agent.phone}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5" data-testid="ls-agent-perms-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-bold text-gray-900">Permissions — {agent.name || agent.phone}</div>
            <div className="text-[12px] text-gray-500 mt-0.5">Select which actions this support agent can perform from the chat sidebar.</div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="space-y-2">
          {PERM_CATALOG.map((p) => {
            const on = selected.has(p.key);
            return (
              <label key={p.key} className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${on ? "border-violet-300 bg-violet-50" : "border-gray-200 hover:bg-gray-50"}`}
                     data-testid={`ls-perm-row-${p.key}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(p.key)} className="mt-0.5 h-4 w-4 accent-violet-600" />
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-gray-900">{p.label}</div>
                  {p.hint && <div className="text-[11.5px] text-gray-500 mt-0.5">{p.hint}</div>}
                  <div className="text-[10.5px] text-gray-400 font-mono mt-1">{p.key}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="ls-perms-save"
                  className="h-10 px-4 rounded-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold">
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateAgentModal({ onClose, onCreated }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !password.trim()) return;
    if (password.length < 4) { toast.error("Password too short"); return; }
    setSaving(true);
    try {
      await api.post("/admin/live-support/agents", {
        phone: phone.trim(),
        password,
        name: name.trim() || phone.trim(),
        designation: designation.trim(),
      });
      toast.success("Agent created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="bg-white rounded-sm shadow-xl w-full max-w-md p-5" data-testid="ls-agent-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-gray-900">Create Live Support Agent</div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Phone Number (login id)</label>
            <input data-testid="ls-agent-phone" value={phone}
                   onChange={(e) => setPhone(e.target.value.replace(/\s+/g, ""))} required
                   placeholder="e.g. 01700000001"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Display Name</label>
            <input data-testid="ls-agent-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Rafiq from Support"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Designation / Role <span className="text-gray-400 normal-case font-normal">(optional)</span></label>
            <input data-testid="ls-agent-designation" value={designation} onChange={(e) => setDesignation(e.target.value)} maxLength={60}
                   placeholder="e.g. Senior Support Manager"
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
            <div className="text-[11px] text-gray-500 mt-1">Shown under the agent&apos;s name in the user&apos;s chat header.</div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Password</label>
            <input data-testid="ls-agent-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required
                   className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-emerald-500 text-gray-900 bg-white" />
            <div className="text-[11px] text-gray-500 mt-1">Share this with the agent — they sign in at /livechat.</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="ls-agent-create-submit"
                  className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold">
            {saving ? "Creating..." : "Create Agent"}
          </button>
        </div>
      </form>
    </div>
  );
}
